import React, { forwardRef, useMemo, useState } from 'react'
import Dialog from '@mui/material/Dialog'
import Slide from '@mui/material/Slide'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTheme } from '@mui/material/styles'
import { OpenInBrowserRounded } from '@mui/icons-material'
import { chromeUrl, detectInAppBrowser } from '../../utils/inAppBrowser'
import {
  gatePaperSx,
  GateBody,
  GateIcon,
  GateTitle,
  GateText,
  GateHint,
  GateActions,
  PrimaryButton,
  SecondaryButton,
  TextButton,
} from './styles'

const SlideUp = forwardRef((props, ref) => <Slide direction="up" ref={ref} {...props} />)
SlideUp.displayName = 'SlideUp'

// Si el enlace se abrió dentro de otra app (Discord, WhatsApp…), pide seguir en Chrome:
// ahí funcionan la ubicación y las notificaciones, y la página no se cierra al salir de la app.
// Si la app Trakku está instalada, el sistema la abre antes con el enlace (public/.well-known)
// y esta página ni carga; este aviso es para cuando eso no pasó.
// No se abre Chrome solo: algunas apps no permiten esos enlaces y mostrarían una página de error.
function OpenInBrowserGate() {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const platform = useMemo(() => detectInAppBrowser(), [])
  const [open, setOpen] = useState(Boolean(platform))
  const [copied, setCopied] = useState(false)

  if (!platform) return null

  const copyLink = async () => {
    try {
      await window.navigator.clipboard.writeText(window.location.href)
      setCopied(true)
    } catch (error) {
      window.prompt('Copia este enlace y ábrelo en Chrome:', window.location.href)
    }
  }

  return (
    <Dialog
      open={open}
      onClose={() => setOpen(false)}
      TransitionComponent={isMobile ? SlideUp : undefined}
      PaperProps={{ sx: gatePaperSx(isMobile) }}
      aria-labelledby="open-in-browser-title"
    >
      <GateBody>
        <GateIcon aria-hidden>
          <OpenInBrowserRounded />
        </GateIcon>
        <GateTitle id="open-in-browser-title" component="h2">
          Abre Trakku en Chrome
        </GateTitle>
        <GateText>
          Estás viendo Trakku dentro de otra app. En Chrome funcionan tu ubicación y
          los avisos de tu moto, y no se cierra al salir de la app.
        </GateText>
        <GateActions>
          <PrimaryButton
            variant="contained"
            disableElevation
            component="a"
            href={chromeUrl(platform)}
          >
            Abrir en Chrome
          </PrimaryButton>
          <SecondaryButton variant="outlined" onClick={copyLink}>
            {copied ? '¡Enlace copiado!' : 'Copiar enlace'}
          </SecondaryButton>
          <TextButton onClick={() => setOpen(false)}>Seguir aquí</TextButton>
        </GateActions>
        <GateHint>
          {platform === 'ios'
            ? 'Si no se abre, toca el menú ⋯ de esta ventana y elige "Abrir en el navegador".'
            : 'Si no se abre, toca el menú ⋮ de esta ventana y elige "Abrir en Chrome".'}
        </GateHint>
      </GateBody>
    </Dialog>
  )
}

export { OpenInBrowserGate }
